"use client";

import LinkToKlardaApp from "@/app/components/LinkToKlardaApp";
import MyCard from "@/app/components/MyCard";
import { CLOUDFRONT_URL } from "@/constant/apiPath";
import useAxiosQuery from "@/hooks/useMyQuery";
import type { NewsResponseCDN, NewsType } from "@/models/news";
import { Skeleton } from "antd/lib";
import dynamic from "next/dynamic";
import Link from "next/link";
import type { PropsWithChildren } from "react";
import { Suspense, useMemo, useRef, useState } from "react";
import type Slider from "react-slick";
import { createSliderSettings } from "../components/slider/SliderSettings";
import React from "react";
import { URL_BY_NEWS_TYPE } from "@/constant";

const SliderComponent = dynamic(
  () => import('@/app/components/slider/SliderWrapper'),
  { ssr: false }
);

const MarketInsight = dynamic(() => import("@/app/components/home/MarketInsight"), { ssr: false });
const MainNews = dynamic(() => import("@/app/components/news/MainNews"), { ssr: false });
const MarketInsightCard = dynamic(() => import("@/app/components/MarketInsightCard"), { ssr: false });
const KlardaReport = dynamic(() => import("@/app/components/home/KlardaReport"), { ssr: false });
const LearnAboutCrypto = dynamic(() => import("@/app/components/home/LearnAboutCrypto"), { ssr: false });
const HotNews = dynamic(() => import("@/app/components/home/HotNews"), { ssr: false });

export default function Home() {
  const mainSliderRef = useRef<Slider | null>(null);
  const [activeMainSlide, setActiveMainSlide] = useState(0);

  const mainSliderSettings = useMemo(
    () => createSliderSettings(mainSliderRef, activeMainSlide, setActiveMainSlide, 4, true),
    [activeMainSlide],
  );

  const { data: latestMoreData, isLoading: isLoadingLatestMore } = useAxiosQuery<NewsResponseCDN>({
    url: `${CLOUDFRONT_URL}/v1/market-insight/news/vi/news/latest`,
    method: "get",
  });

  const { data: reviewData, isLoading: isLoadingReview } = useAxiosQuery<NewsResponseCDN>({
    url: `${CLOUDFRONT_URL}/v1/market-insight/news/vi/review/project`,
    method: "get",
  });

  return (
    <>
      <main className="min-h-screen p-0 md:p-2">
        <div className="flex flex-col gap-4">
          <MyCard
            titleLevel={1}
            title={<TitleLink link="/tin-tuc" title="TIN TỨC MỚI NHẤT" />}
            styles={{ body: { paddingBottom: "0px !important" } }}
          >
            <LoadingSkeleton isLoading={latestMoreData?.news && latestMoreData?.news?.length > 0}>
              <div className="grid grid-cols-1 lg:grid-cols-11 gap-5">
                <div className="col-span-1 lg:col-span-4">
                  <Suspense fallback={<Skeleton active />}>
                    <MainNews news={latestMoreData?.news?.[0]} isLoading={isLoadingLatestMore} isLargeTitle url={URL_BY_NEWS_TYPE[latestMoreData?.news?.[0]?.klarda_categories as NewsType] || URL_BY_NEWS_TYPE["Klarda News"]}/>
                  </Suspense>
                </div>

                <div className="col-span-1 lg:col-span-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                    {latestMoreData?.news?.slice(1, 5).map((news, i) => (
                      <MainNews key={news.id ?? "" + i} news={news} isHideDesc isLoading={isLoadingLatestMore} url={URL_BY_NEWS_TYPE[news?.klarda_categories as NewsType] || URL_BY_NEWS_TYPE["Klarda News"]} />
                    ))}
                  </div>
                </div>

                <div className="hidden lg:block col-span-1 lg:col-span-3">
                  <MarketInsightCard
                    apiUrl={`${CLOUDFRONT_URL}/v1/market-insight/news/vi/news/latest`}
                    limit={7}
                    isHideLink
                    isHideFirstThumbnail
                  />
                </div>
              </div>
              <LinkToKlardaApp isOnlyText url="/tin-tuc" className="mt-2 lg:mt-0 block" />
            </LoadingSkeleton>
          </MyCard>

          <MyCard
            titleLevel={2}
            title={<TitleLink link="/review" title="KLARDA REVIEW" />}
            styles={{ body: { paddingBottom: "0px !important" } }}
          >
            <LoadingSkeleton isLoading={latestMoreData?.news && latestMoreData?.news?.length > 0}>
              <div className="px-6 md:px-2">
                <SliderComponent {...mainSliderSettings} ref={mainSliderRef} className="p-2">
                  {reviewData?.news?.map((news, i) => (
                    <MainNews
                      cardClassName="px-3 mb-2"
                      key={news.id ?? "" + i}
                      news={news}
                      isLoading={isLoadingReview}
                      url={URL_BY_NEWS_TYPE[news?.klarda_categories as NewsType] || URL_BY_NEWS_TYPE["Klarda News"]}
                    />
                  ))}
                </SliderComponent>
                {!isLoadingReview && (
                  <LinkToKlardaApp isOnlyText label="Xem thêm review khác >" url="/review" className="ml-5" />
                )}
              </div>
            </LoadingSkeleton>
          </MyCard>

          <MyCard
            titleLevel={2}
            title={<TitleLink link="/review" title="INSIGHT" />}
            styles={{ body: { paddingBottom: "0px !important" } }}
          >
            <LoadingSkeleton isLoading={latestMoreData?.news && latestMoreData?.news?.length > 0}>
              <MarketInsight />
            </LoadingSkeleton>
          </MyCard>

          <MyCard titleLevel={2} title={<TitleLink link="/report" title="MARKET REPORTS" />}>
            <LoadingSkeleton isLoading={latestMoreData?.news && latestMoreData?.news?.length > 0}>
              <KlardaReport />
            </LoadingSkeleton>
          </MyCard>

          {/* <AdUnit slot="7230169083" /> */}

          <MyCard titleLevel={2} title={<TitleLink link="/crypto" title="HỌC ĐẦU TƯ CRYPTO" />}>
            <LoadingSkeleton isLoading={latestMoreData?.news && latestMoreData?.news?.length > 0}>
              <LearnAboutCrypto />
            </LoadingSkeleton>
          </MyCard>

          <MyCard titleLevel={2} title={<TitleLink link="/tin-tuc" title="TIN NỔI BẬT" />}>
            <LoadingSkeleton isLoading={latestMoreData?.news && latestMoreData?.news?.length > 0}>
              <HotNews />
            </LoadingSkeleton>
          </MyCard>

          {/* author */}
          {/* <Suspense fallback={<Skeleton active />}>
            <LoadingSkeleton isLoading={latestMoreData?.news && latestMoreData?.news?.length > 0}>
              <div className="py-0 px-0 md:py-4 md:px-4">
                <h2 className="text-center text-2xl md:text-4xl font-extrabold text-gray-900">
                  <TitleLink title="Tác giả nổi bật" link="/tac-gia" />{" "}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-12 gap-6 md:gap-12 mt-10 mb-4">
                  {authorData?.data?.slice(0, 8)?.map(author => (
                    <div className="col-span-1 md:col-span-3" key={author?.user_id?.profile_information?.display_name}>
                      <AuthorCard data={author} />
                    </div>
                  ))}
                </div>
                <LinkToKlardaApp isOnlyText label="Xem toàn bộ tác giả >" url="/tac-gia" className="ml-6 md:ml-12" />
              </div>
            </LoadingSkeleton>
          </Suspense> */}
        </div>
      </main>
    </>
  );
}

function TitleLink({ title, link }: { title: string; link: string }) {
  return (
    <Link className="text-inherit hover:underline" href={link}>
      {title}
    </Link>
  );
}

function LoadingSkeleton({ isLoading = false, children }: PropsWithChildren & { isLoading?: boolean }) {
  return <>{isLoading ? <>{children}</> : <Skeleton active paragraph={{ rows: 6 }} />}</>;
}

